import { createContext, useContext } from 'react';
import { APIError, SessionImageExpiredError } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import type { MediaThumbnailState } from './MediaThumbnail';
import { loadMediaPreview, previewFormatFor } from './media-preview';

export interface ChatImageScope {
  sessionId: string | null;
  workspaceId: string | null;
  thumbnails: Map<string, Promise<MediaThumbnailState>>;
}

export const IMAGE_TOOLS = new Set(['diagram_render', 'image_read']);

export const ChatImageScopeContext = createContext<ChatImageScope | null>(null);

export function useChatImageScope(): ChatImageScope {
  const scope = useContext(ChatImageScopeContext);
  if (!scope) {
    throw new Error('useChatImageScope: no ChatImageScopeContext provider above this component');
  }
  return scope;
}

export function newChatImageScope(sessionId: string | null, workspaceId: string | null): ChatImageScope {
  return { sessionId, workspaceId, thumbnails: new Map() };
}

export function releaseChatImageScope(scope: ChatImageScope): void {
  for (const pending of scope.thumbnails.values()) {
    void pending.then((state) => {
      if (state.kind === 'ready') URL.revokeObjectURL(state.url);
    });
  }
  scope.thumbnails.clear();
}

const MEDIA_HANDLE = /^media:\/\/([0-9a-f-]{36})(?:@v(\d+))?$/i;
const IMAGE_HANDLE = /^image:\/\/([0-9a-f-]{36})$/i;

export function isImageHandle(handle: string): boolean {
  return MEDIA_HANDLE.test(handle) || IMAGE_HANDLE.test(handle);
}

async function loadMediaThumbnail(id: string, pinned: number | undefined): Promise<MediaThumbnailState> {
  let object;
  try {
    object = await getSDK().media.get(id);
  } catch (err) {
    if (err instanceof APIError && err.status === 404) return { kind: 'deleted' };
    throw err;
  }
  if (object.deleted_at) return { kind: 'deleted' };
  const version = pinned ?? object.current_version;
  const row = object.versions?.find((v) => v.version === version);
  if (!row) {
    throw new Error(`media ${id} has no version ${version}`);
  }
  if (!row.formats) {
    throw new Error(`media ${id} version ${version} came back without its formats`);
  }
  const format = previewFormatFor(
    row.formats.map((f) => f.format),
    row.warnings,
  );
  if (!format) return { kind: 'unpreviewable' };
  return { kind: 'ready', url: await loadMediaPreview(id, format, version), previewKind: format };
}

// (sessions ADR-9166f)
function readQualityKind(contentType: string): 'jpeg' | 'png' {
  const mime = contentType.split(';')[0]!.trim();
  if (mime === 'image/jpeg') return 'jpeg';
  if (mime === 'image/png') return 'png';
  throw new Error(`a session image read-quality copy is ${mime}, neither JPEG nor PNG`);
}

async function loadSessionImageThumbnail(scope: ChatImageScope, imageId: string): Promise<MediaThumbnailState> {
  if (!scope.sessionId) {
    throw new Error('an image:// thumbnail needs the chat session id');
  }
  try {
    const image = await getSDK().sessions.imageContent(scope.sessionId, imageId, { workspaceId: scope.workspaceId });
    const kind = readQualityKind(image.contentType);
    return { kind: 'ready', url: URL.createObjectURL(new Blob([image.bytes], { type: `image/${kind}` })), previewKind: kind };
  } catch (err) {
    if (err instanceof SessionImageExpiredError) return { kind: 'expired' };
    throw err;
  }
}

export function thumbnailFor(scope: ChatImageScope, handle: string): Promise<MediaThumbnailState> {
  const cached = scope.thumbnails.get(handle);
  if (cached) return cached;
  const media = MEDIA_HANDLE.exec(handle);
  const image = IMAGE_HANDLE.exec(handle);
  let pending: Promise<MediaThumbnailState>;
  if (media) {
    pending = loadMediaThumbnail(media[1]!, media[2] ? Number(media[2]) : undefined);
  } else if (image) {
    pending = loadSessionImageThumbnail(scope, image[1]!);
  } else {
    pending = Promise.reject(new Error(`not an image handle: ${handle}`));
  }
  const settled = pending.catch((err: unknown): MediaThumbnailState => {
    if (scope.thumbnails.get(handle) === settled) scope.thumbnails.delete(handle);
    return { kind: 'failed', message: err instanceof Error ? err.message : String(err) };
  });
  scope.thumbnails.set(handle, settled);
  return settled;
}
